import { Button, FormGroup, FormParagraph } from '@ksyos/design-system';
import { Link as ReactRouterLink } from '@ksyos/router';
import type { IOrganizationGeneralSettings } from '@common/validation/organization';
import type { ValidationErrors } from '@frontend/utils';
import { OrganizationRelationshipType } from '@common/enums';
import { emptyUuid } from '@common/utils/uuid';
import { getSelectedLanguage } from '@frontend/translate';
import type { OrganizationRelationshipAction } from '../utils/organizationRelationshipsReducer';
import { OrganizationRelationshipFormComponent } from '../OrganizationRelationshipFormComponent';
import { OrganizationRelationshipsActions } from '../utils/organizationRelationshipsReducer';

type OrganizationRelationshipsFormGroupProps = {
    canEdit: boolean;
    dispatchRelationshipStateAction: React.Dispatch<OrganizationRelationshipAction<IOrganizationGeneralSettings>>;
    errors?: ValidationErrors;
    onRemoveValidationErrors?: (keys: string[]) => void;
    organization: IOrganizationGeneralSettings;
    organizationId: string;
};

export function OrganizationRelationshipsFormGroup({
    canEdit,
    dispatchRelationshipStateAction,
    organization,
    organizationId,
    errors,
    onRemoveValidationErrors,
}: Readonly<OrganizationRelationshipsFormGroupProps>) {
    const translate = getSelectedLanguage();
    const { organizationRelationships } = organization;
    if (!organizationRelationships.length && !canEdit) {
        return null;
    }

    function hasOrganizationRelationshipsError() {
        return errors?.keySeq().some((key) => key?.startsWith('organizationRelationships') === true);
    }

    return (
        <>
            {canEdit && (
                <>
                    <FormGroup
                        hasErrors={hasOrganizationRelationshipsError()}
                        isRequired={organization.organizationRelationships.length >= 1}
                        label={translate.organization.organizationRelationships}
                    >
                        {organizationRelationships.map((organizationRelationship, index) => (
                            <OrganizationRelationshipFormComponent<IOrganizationGeneralSettings>
                                dispatchRelationshipAction={dispatchRelationshipStateAction}
                                index={index}
                                key={`relation-${index}-${organizationRelationship.id}`}
                                onRemoveValidationErrors={onRemoveValidationErrors}
                                organization={organization}
                                organizationId={organizationId}
                                organizationRelationship={organizationRelationship}
                                relationshipsErrors={errors}
                            />
                        ))}
                    </FormGroup>
                    <Button
                        iconLeft="add"
                        onClick={() => {
                            dispatchRelationshipStateAction({
                                payload: {
                                    relatedOrganizationId: emptyUuid,
                                    type: OrganizationRelationshipType.isBranchOf,
                                },
                                type: OrganizationRelationshipsActions.addRelationship,
                            });
                        }}
                        variant="link"
                    >
                        {translate.organization.addOrganizationRelationship}
                    </Button>
                </>
            )}

            {!canEdit && organizationRelationships.length > 0 && (
                <FormGroup label={translate.organization.organizationRelationships}>
                    {organizationRelationships
                        .filter((item) => item.relatedOrganization)
                        .map((organizationRelationship, index) => (
                            <FormParagraph key={`relation-${index}`}>
                                {translate.organizationRelationshipTypeEnum[organizationRelationship.type]}{' '}
                                <Button
                                    as={ReactRouterLink}
                                    to={`/organizations/${organizationRelationship.relatedOrganizationId}/view/details`}
                                    variant="link"
                                >
                                    {organizationRelationship.relatedOrganization?.name}
                                </Button>
                            </FormParagraph>
                        ))}
                </FormGroup>
            )}
        </>
    );
}
